import { useRuntimeConfig } from '#app'
import { createError } from 'nuxt/app'
import { CraftUtil } from '../util/craft-util'
import { AUTHORISED_CONTENT_ENABLED } from '../util/features'

// Singleton lock: ensures only one /api/csrf-refresh call is in-flight at a time.
// Without this, concurrent 403s each fire their own refresh, the cookie gets
// overwritten multiple times, and at least one retry ends up with a stale token.
let refreshPromise: Promise<void> | null = null

/**
 * Check fetch response for errors. Throws on hard failure, logs partial errors.
 * In dev mode, partial GraphQL errors also throw for visibility.
 */
function handleFetchErrors(data: any, error: any, label: string) {
    if (error) {
        console.error(`${label} failed`, error)
        throw createError({
            statusCode: error.statusCode ?? 502,
            statusMessage: error.message ?? `${label} failed`,
        })
    }

    if (data?.errors) {
        if (import.meta.dev) {
            throw createError({
                statusCode: 500,
                statusMessage: `${label} returned partial errors`,
                data: data.errors,
            })
        } else {
            console.error(`${label} returned partial errors:`, data.errors)
        }
    }
}

function logQueryTiming(key: string | undefined, startTime: number) {
    console.groupCollapsed(`${key} query in`, `${(Date.now() / 1000 - startTime).toFixed(2)}s`)
    console.groupEnd()
}

/**
 * Composable for making requests to the Craft CMS API.
 *
 * Architecture:
 * - SSR: Fetches directly from the CMS using credentials from CloudFront headers (no extra hop).
 * - CSR: Fetches via same-origin Nuxt server proxy routes (/api/graphql, /api/cms/*),
 *        which inject the bearer token server-side. The token never reaches the browser.
 *
 * CMS URLs (adminUrl, craftUrl) are transferred from SSR → CSR via useState (no secrets).
 * CSRF protection: Tokens are fetched client-side via /api/csrf-refresh on app boot (csrf.client.ts).
 *                  Double-submit cookie pattern guards proxy routes via csrf middleware.
 *                  On 403 (expired/missing token), auto-refreshes token and retries (once).
 */
export function useGraphQL() {
    const runtimeConfig = useRuntimeConfig()
    const nuxtApp = useNuxtApp()

    // CSRF token for proxy requests (fetched by csrf.client.ts plugin on app boot, validated by csrf middleware)
    const csrfToken = useState<string>('csrf-token')

    // SSR-only: full config with token for direct CMS access
    let serverConfig: CraftCmsConfig | null = null

    if (import.meta.server) {
        const headers = nuxtApp.ssrContext.event.node.req.headers
        serverConfig = CraftUtil.getServerAuthConfig(runtimeConfig, headers)

        if (!serverConfig.endpoint || !serverConfig.token) {
            throw createError({
                statusCode: 500,
                statusMessage: 'GraphQL endpoint or token missing.',
            })
        }
    }

    // Transfer non-secret CMS URLs from SSR → CSR via useState (replaces the cookie approach)
    const adminUrl = useState<string>('cms-admin-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '/admin') : ''
    })
    const craftUrl = useState<string>('cms-craft-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '') : ''
    })
    const cmsApiUrl = useState<string>('cms-api-url', () => {
        return serverConfig?.endpoint || ''
    })

    /**
     * Refresh CSRF token from server and update local state.
     * Called when a CSR request returns 403 Forbidden (token expired/missing).
     * Uses a module-level promise lock so concurrent 403s share a single refresh call.
     */
    const refreshCsrfToken = async (): Promise<void> => {
        if (refreshPromise) {
            return refreshPromise
        }

        refreshPromise = $fetch<{ token: string }>('/api/csrf-refresh')
            .then((response) => {
                csrfToken.value = response.token
            })
            .catch((error) => {
                console.error('Failed to refresh CSRF token:', error)
            })
            .finally(() => {
                refreshPromise = null
            })

        return refreshPromise
    }

    /**
     * $fetch wrapper that retries once on 403 after refreshing the CSRF token.
     * Used for client-only requests (server === false) and inside useAsyncData handlers.
     */
    const fetchWithCsrfRetry = async (url: string, fetchOptions: Record<string, any>) => {
        try {
            return await $fetch(url, fetchOptions)
        } catch (error: unknown) {
            if (error?.status === 403) {
                await refreshCsrfToken()
                fetchOptions.headers = {
                    ...fetchOptions.headers,
                    'X-CSRF-Token': csrfToken.value,
                }
                return $fetch(url, fetchOptions)
            }

            throw createError(error)
        }
    }

    const query = async ({ query, variables = {}, ...options }) => {
        const { isPreview, previewToken, livePreviewToken, previewTimestamp } = usePreview()

        const beforeFetchStamp = Date.now() / 1000

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
            headers['X-Craft-Preview-Token'] = livePreviewToken.value
            if (options.key) {
                options.key += `-${previewTimestamp.value}`
            }
        }

        // Client-only path: raw $fetch is preferred over useAsyncData({ server: false })
        // for event-driven calls (gate auth, token validation) that don't need reactive refs or hydration
        if (options.server === false) {
            if (import.meta.server) return null

            headers['X-CSRF-Token'] = csrfToken.value
            const response = await fetchWithCsrfRetry('/api/graphql', {
                method: 'POST' as const,
                headers,
                body: { query, variables },
            })
            return response.data
        }

        // SSR-hydrated path: useAsyncData ensures data is fetched once and transferred to client
        const { data, error } = await useAsyncData(options.key, async () => {
            if (import.meta.server && serverConfig) {
                headers['Authorization'] = `Bearer ${serverConfig.token}`
                return $fetch(serverConfig.endpoint, {
                    method: 'POST' as const,
                    headers,
                    body: { query, variables },
                })
            }

            // CSR: fetch via same-origin server proxy (token stays server-side)
            headers['X-CSRF-Token'] = csrfToken.value
            return fetchWithCsrfRetry('/api/graphql', {
                method: 'POST' as const,
                headers,
                body: { query, variables },
            })
        })

        handleFetchErrors(data.value, error.value, 'GraphQL request')
        logQueryTiming(options.key, beforeFetchStamp)

        return data.value?.data
    }

    const cmsQuery = async ({ uri, variables = {}, routeQuery = null, ...options }) => {
        const { isPreview, previewToken, livePreviewToken, previewTimestamp } = usePreview(routeQuery)

        if (isPreview.value) {
            // Needed for cache busting in preview mode
            const separator = uri.includes('?') ? '&' : '?'
            uri += `${separator}previewTimestamp=${previewTimestamp.value}`

            //add the previewTimestamp to the key
            if (options.key) {
                options.key += `-${previewTimestamp.value}`
            }
        } else {
            // If a `routeQuery` was provided by middleware, use it to check for `no-cache`.
            let rq = routeQuery
            if (!rq) {
                const route = useRoute()
                rq = route.query ?? {}
            }

            if (rq && 'no-cache' in rq) {
                const separator = uri.includes('?') ? '&' : '?'
                uri += `${separator}no-cache=${Date.now()}`
            }
        }

        const beforeFetchStamp = Date.now() / 1000

        const headers: Record<string, string> = {
            'Cache-Control': 'public',
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
            headers['X-Craft-Preview-Token'] = livePreviewToken.value
        }

        // Client-only path: raw $fetch is preferred over useAsyncData({ server: false })
        // for event-driven calls (gate auth, token validation) that don't need reactive refs or hydration
        if (options.server === false) {
            if (import.meta.server) return { data: null }

            headers['X-CSRF-Token'] = csrfToken.value
            if (AUTHORISED_CONTENT_ENABLED) {
                const gateTokens = Object.values(getStoredGateTokens())
                if (gateTokens.length > 0) {
                    headers['X-Gate-Token'] = gateTokens.join(',')
                }
            }

            const response = await fetchWithCsrfRetry(`/api/cms/${uri}`, {
                ...options,
                headers,
                query: variables,
            })
            return { data: response.data }
        }

        // SSR-hydrated path: useAsyncData ensures data is fetched once and transferred to client
        const { data, error } = await useAsyncData(options.key, async () => {
            if (import.meta.server && serverConfig) {
                const dataApiEndpoint = replace(serverConfig.endpoint, '/api', '/data-api')
                headers['Authorization'] = `Bearer ${serverConfig.token}`

                // Forward mock scenario header from cookie when in mock mode
                const e2eCookie = useCookie('__e2e_scenario')
                if (e2eCookie.value) {
                    headers['X-E2E-Scenario'] = typeof e2eCookie.value === 'string'
                        ? e2eCookie.value
                        : JSON.stringify(e2eCookie.value)
                }

                return $fetch(`${dataApiEndpoint}/${uri}`, {
                    headers,
                    query: variables,
                })
            }

            // CSR: fetch via same-origin server proxy (token stays server-side)
            headers['X-CSRF-Token'] = csrfToken.value

            // Include gate tokens for protected content access (client-side only)
            if (AUTHORISED_CONTENT_ENABLED) {
                const gateTokens = Object.values(getStoredGateTokens())
                if (gateTokens.length > 0) {
                    headers['X-Gate-Token'] = gateTokens.join(',')
                }
            }

            return fetchWithCsrfRetry(`/api/cms/${uri}`, {
                headers,
                query: variables,
            })
        })

        handleFetchErrors(data.value, error.value, 'CMS data request')
        logQueryTiming(options.key, beforeFetchStamp)

        return { data }
    }

    return {
        apiUrl: cmsApiUrl.value,
        adminUrl: adminUrl.value,
        craftUrl: craftUrl.value,
        query,
        cmsQuery
    }
}
